import React, { useEffect, useRef } from "react";
import { useHome } from "../providers/HomeContext";

// Import assets
import landImgSrc from "../assets/images/land.png";
import run0 from "../assets/images/run_0.png";
import run1 from "../assets/images/run_1.png";
import run2 from "../assets/images/run_2.png";
import run3 from "../assets/images/run_3.png";
import run4 from "../assets/images/run_4.png";
import run5 from "../assets/images/run_5.png";
import run6 from "../assets/images/run_6.png";
import run7 from "../assets/images/run_7.png";
import jumpImgSrc from "../assets/images/jump.png";
import landingImgSrc from "../assets/images/landing.png";

const RUN_SPRITES = [run0, run1, run2, run3, run4, run5, run6, run7];

export const RunnerGame: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { currentPage } = useHome();

  // Animation & Physics parameters
  const speed = 400; // pixels per second
  const gravity = 2000; // pixels per second^2
  const initialJumpVelocity = -850; // pixels per second

  // Refs to hold state values for the game loop (avoids re-triggering useEffect)
  const gameStateRef = useRef({
    groundX: 0,
    manY: 0, // y coordinate relative to ground (0 = on ground)
    jumpVelocity: 0,
    isJumping: false,
    frameIndex: 0,
    frameTime: 0,
    lastTime: 0,
    imagesLoaded: false,
  });

  // Image elements cache
  const imagesRef = useRef<{
    land: HTMLImageElement | null;
    man: HTMLImageElement[];
    jump: HTMLImageElement | null;
    landing: HTMLImageElement | null;
  }>({
    land: null,
    man: [],
    jump: null,
    landing: null,
  });

  // Preload images
  useEffect(() => {
    let loadedCount = 0;
    const totalImages = RUN_SPRITES.length + 3; // land, jump, landing + RUN_SPRITES

    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === totalImages) {
        gameStateRef.current.imagesLoaded = true;
      }
    };

    // Load land
    const landImg = new Image();
    landImg.src = landImgSrc;
    landImg.onload = checkAllLoaded;
    imagesRef.current.land = landImg;

    // Load jump sprite
    const jumpImg = new Image();
    jumpImg.src = jumpImgSrc;
    jumpImg.onload = checkAllLoaded;
    imagesRef.current.jump = jumpImg;

    // Load landing sprite
    const landingImg = new Image();
    landingImg.src = landingImgSrc;
    landingImg.onload = checkAllLoaded;
    imagesRef.current.landing = landingImg;

    // Load man frames
    imagesRef.current.man = RUN_SPRITES.map((src) => {
      const img = new Image();
      img.src = src;
      img.onload = checkAllLoaded;
      return img;
    });
  }, []);

  // Trigger jump
  const triggerJump = () => {
    const state = gameStateRef.current;
    if (!state.isJumping) {
      state.isJumping = true;
      state.jumpVelocity = initialJumpVelocity;
    }
  };

  // Controls: Keyboard & Click (only active when on the credit page)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (currentPage === 3 && (e.key === " " || e.key === "ArrowUp")) {
        e.preventDefault();
        triggerJump();
      }
    };

    const handleWindowClick = (e: MouseEvent) => {
      if (currentPage === 3) {
        const target = e.target as HTMLElement;
        // Don't trigger a jump if clicking interactive buttons, links, or cards
        if (
          target.closest("a") ||
          target.closest("button") ||
          target.closest(".cursor-pointer")
        ) {
          return;
        }
        triggerJump();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("click", handleWindowClick);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("click", handleWindowClick);
    };
  }, [currentPage]);

  // Main Canvas Render Loop
  useEffect(() => {
    let animationId: number;

    const updateAndRender = (time: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Disable image smoothing to render pixel art crisply (nearest neighbor filtering)
      ctx.imageSmoothingEnabled = false;

      const state = gameStateRef.current;
      if (state.lastTime === 0) {
        state.lastTime = time;
      }
      
      const dt = Math.min((time - state.lastTime) / 1000, 0.1); // clamp delta time to avoid huge leaps
      state.lastTime = time;

      // Clear Canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (state.imagesLoaded) {
        const landSpriteWidth = 128;
        const landSpriteHeight = 128;
        
        // Ground positions
        const canvasHeight = canvas.height;
        const groundLevelY = canvasHeight - landSpriteHeight + 16;

        // 1. Update Ground (loops horizontally)
        state.groundX -= speed * dt;
        if (state.groundX <= -landSpriteWidth) {
          state.groundX += landSpriteWidth;
        }

        // Draw Ground tiles
        if (imagesRef.current.land) {
          const tilesNeeded = Math.ceil(canvas.width / landSpriteWidth) + 2;
          for (let i = 0; i < tilesNeeded; i++) {
            ctx.drawImage(
              imagesRef.current.land,
              state.groundX + i * landSpriteWidth,
              groundLevelY,
              landSpriteWidth,
              landSpriteHeight
            );
          }
        }

        // 2. Update Man physics
        if (state.isJumping) {
          state.manY += state.jumpVelocity * dt;
          state.jumpVelocity += gravity * dt;

          if (state.manY >= 0) {
            // Landed
            state.manY = 0;
            state.jumpVelocity = 0;
            state.isJumping = false;
          }
        }

        // 3. Update Man running animation frame
        if (!state.isJumping) {
          state.frameTime += dt;
          if (state.frameTime >= 0.08) { // 80ms per frame for a smooth fast run
            state.frameIndex = (state.frameIndex + 1) % RUN_SPRITES.length;
            state.frameTime = 0;
          }
        }

        // 4. Draw Man character
        let manImg: HTMLImageElement | null = null;
        if (state.isJumping) {
          if (state.jumpVelocity < 0) {
            manImg = imagesRef.current.jump;
          } else {
            manImg = imagesRef.current.landing;
          }
        } else {
          manImg = imagesRef.current.man[state.frameIndex];
        }

        if (manImg) {
          // Exact Flutter height: 165. Width is calculated dynamically to preserve aspect ratio.
          const manHeight = 165;
          const aspectRatio = manImg.naturalWidth / manImg.naturalHeight;
          const manWidth = manHeight * aspectRatio;
          
          // Center-align the character horizontally on the canvas
          const manX = (canvas.width - manWidth) / 2;
          
          // Draw character. The manY is added to groundLevelY (sitting 12px into grass)
          ctx.drawImage(
            manImg,
            manX,
            groundLevelY - manHeight + state.manY + 12,
            manWidth,
            manHeight
          );
        }
      } else {
        // Draw loading indicator
        const isDark = document.documentElement.classList.contains("dark");
        ctx.fillStyle = isDark ? "#F5F5F5" : "#2A2A2A";
        ctx.font = "16px sans-serif";
        ctx.fillText("Loading Game...", 20, 40);
      }

      animationId = requestAnimationFrame(updateAndRender);
    };

    animationId = requestAnimationFrame((time) => {
      gameStateRef.current.lastTime = time;
      updateAndRender(time);
    });

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, []);

  // Update canvas width dynamically on resize
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (canvas && canvas.parentElement) {
        canvas.width = canvas.parentElement.clientWidth;
        canvas.height = 450; // set standard height matching container
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className="w-full h-[450px] overflow-hidden select-none relative z-30 pointer-events-none">
      <canvas
        ref={canvasRef}
        className="block w-full h-full [image-rendering:pixelated] dark:brightness-[0.6] dark:contrast-[1.15] transition-all duration-300"
        title="Press Spacebar or click empty space to jump!"
      />
    </div>
  );
};
